import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Search, X } from 'lucide-react';
import { Input } from '@/shared/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/sheet';
import { cn } from '@/shared/lib/cn';
import type { CurrencyCode } from '@/shared/lib/format';
import type { CatalogProduct } from '../model/operator-payload';
import type { OperatorProgram } from '../model/operator-rules';
import { catalogProductLabel, isCatalogProductEnabled } from './operator-catalog-label';
import { normalizeQuery } from './operator-product-picker';
import type { Passbook } from '@/shared/types';

interface OperatorCatalogPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: CatalogProduct[];
  loading?: boolean;
  selectedProduct?: CatalogProduct | null;
  onSelectProduct: (p: CatalogProduct | null) => void;
  currencyCode: CurrencyCode;
  program: OperatorProgram;
  passbook?: Passbook;
  title: string;
}

function matchesProduct(p: CatalogProduct, q: string): boolean {
  if (!q) return true;
  const name = normalizeQuery(p.name ?? '');
  const sku = normalizeQuery(p.sku ?? '');
  return name.includes(q) || sku.includes(q);
}

function productValueNumber(p: CatalogProduct): number {
  const n = typeof p.value === 'number' ? p.value : parseFloat(String(p.value ?? 0));
  return Number.isFinite(n) ? n : 0;
}

/** Ordena por `value` crescente; empate cai pro alfabético (nome, pt-BR). */
function compareByValueThenName(a: CatalogProduct, b: CatalogProduct): number {
  const diff = productValueNumber(a) - productValueNumber(b);
  if (diff !== 0) return diff;
  return (a.name ?? '').localeCompare(b.name ?? '', 'pt-BR');
}

/** Picker de resgate do catálogo — busca + Sheet, mesmo padrão do OperatorProductPicker. */
export function OperatorCatalogPicker({
  open,
  onOpenChange,
  products,
  loading,
  selectedProduct,
  onSelectProduct,
  currencyCode,
  program,
  passbook,
  title,
}: OperatorCatalogPickerProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const normalized = normalizeQuery(query);

  const enabledProducts = useMemo(
    () => products.filter(isCatalogProductEnabled).sort(compareByValueThenName),
    [products],
  );
  const filtered = useMemo(
    () => enabledProducts.filter((p) => matchesProduct(p, normalized)),
    [enabledProducts, normalized],
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const t = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 50);
    return () => window.clearTimeout(t);
  }, [open]);

  const handleSelect = (p: CatalogProduct) => {
    onSelectProduct(selectedProduct?._id === p._id ? null : p);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <SheetHeader className="mb-0 shrink-0 space-y-1 border-b border-[hsl(var(--glass-border)/0.4)] px-5 py-4 text-left">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>Busque por nome ou SKU.</SheetDescription>
        </SheetHeader>

        <div className="shrink-0 border-b border-[hsl(var(--glass-border)/0.35)] px-4 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Nome ou SKU…"
              className="h-11 rounded-2xl pl-10 pr-10 text-base"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {query ? (
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                onClick={() => {
                  setQuery('');
                  inputRef.current?.focus();
                }}
                aria-label="Limpar busca"
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-3 py-2">
          {loading ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">Carregando catálogo…</p>
          ) : enabledProducts.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              Nenhum produto elegível no catálogo desta loja.
            </p>
          ) : filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              Nenhum resultado para “{query.trim()}”.
            </p>
          ) : (
            <ul className="space-y-1">
              {filtered.map((p) => {
                const selected = selectedProduct?._id === p._id;
                return (
                  <li key={p._id}>
                    <button
                      type="button"
                      className={cn(
                        'flex w-full items-center gap-2 rounded-xl px-2.5 py-2.5 text-left',
                        selected ? 'bg-primary/10' : 'hover:bg-accent/50',
                      )}
                      onClick={() => handleSelect(p)}
                    >
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">
                        {catalogProductLabel(p, currencyCode, program, passbook)}
                      </span>
                      {selected ? <Check className="size-4 shrink-0 text-primary" /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
